/**
 * RichText-Renderer als pure Function — testbar ohne DOM.
 *
 * Pipeline (SCHEMA v2): Karten-Content ist Markdown (GFM + breaks) mit
 *   - `$…$` / `$$…$$`   → KaTeX-Platzhalter (lazy hydriert, s. hydrate.ts)
 *   - ```mermaid-Fence  → Mermaid-Platzhalter (lazy hydriert)
 *   - ![alt](src)       → <img> (Standard-Markdown)
 *
 * Mathe/Mermaid werden VOR marked in Platzhalter gestasht, damit Markdown
 * TeX-Underscores bzw. `-->`-Pfeile nicht zerlegt. Platzhalter tragen den
 * Quelltext URI-encoded im data-Attribut; der sichtbare Fallback ist der
 * escapte Quelltext (lesbar, solange KaTeX/Mermaid noch nicht geladen sind).
 *
 * Vertrauensgrenze: Content stammt ausschließlich aus cards/*.md im Repo
 * und wird von tools/build_cards.py validiert (kein Fremd-HTML) — daher
 * kein Runtime-Sanitizer.
 */
import { marked } from 'marked';

marked.use({ gfm: true, breaks: true });

const INLINE_MATH = /\$(\S(?:[^$\n]*?\S)?)\$/g;
const DISPLAY_MATH = /\$\$([\s\S]+?)\$\$/g;
const MERMAID_FENCE = /```mermaid\n([\s\S]*?)```/g;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function placeholder(i: number): string {
  return `%%RT${i}%%`;
}

export function renderRichText(md?: string | null): string {
  if (!md) return '';
  const stash: string[] = [];

  let src = md.replace(MERMAID_FENCE, (_m, code: string) => {
    const c = code.replace(/\n$/, '');
    stash.push(
      `<div class="rt-mermaid" data-code="${encodeURIComponent(c)}"><pre class="rt-fallback">${escapeHtml(c)}</pre></div>`,
    );
    return placeholder(stash.length - 1);
  });

  src = src.replace(DISPLAY_MATH, (_m, tex: string) => {
    const t = tex.trim();
    stash.push(
      `<div class="rt-math" data-tex="${encodeURIComponent(t)}" data-display="1">${escapeHtml(t)}</div>`,
    );
    return placeholder(stash.length - 1);
  });

  src = src.replace(INLINE_MATH, (_m, tex: string) => {
    stash.push(
      `<span class="rt-math" data-tex="${encodeURIComponent(tex)}" data-display="0">${escapeHtml(tex)}</span>`,
    );
    return placeholder(stash.length - 1);
  });

  let html = (marked.parse(src, { async: false }) as string).trim();

  // Block-Platzhalter, die marked in ein <p> gewickelt hat, wieder auspacken —
  // sonst landet ein <div> im <p> (invalides Nesting).
  html = html.replace(/<p>(%%RT\d+%%)<\/p>/g, '$1');
  html = html.replace(/%%RT(\d+)%%/g, (_m, i: string) => stash[Number(i)] ?? '');
  return html;
}

/** Markdown → reiner Sprech-/Suchtext (für TTS und Volltext-Filter). */
export function richTextToPlain(md?: string | null): string {
  // Tags ersatzlos strippen — marked trennt Blöcke bereits mit Newlines,
  // sodass keine Wörter zusammenkleben; \s+ kollabiert danach.
  return renderRichText(md)
    .replace(/<[^>]+>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}
